// Per-user localStorage namespace.
//
// Every key the app stores goes through userStorage.get/set/remove, which
// prefixes it with K_${currentUserId}_. So two Supabase users on the same
// browser write to completely disjoint localStorage entries.
//
// Wiring:
//   - The auth provider calls setCurrentUser(user.id) on sign-in and
//     setCurrentUser(null) on sign-out.
//   - Storage callers (storageApi inside CommissionTracker) call get/set
//     without thinking about the user — userStorage does the prefixing.
//
// Migration (Phase A1.6):
//   - migrateBrowserData(userId) is now a flag-only no-op. We do NOT claim
//     any pre-existing un-prefixed legacy keys for the first user — every
//     user starts with an empty namespace, including the first user on a
//     browser that has data left over from the pre-Supabase build.
//   - The BROWSER_MIGRATED / USER_MIGRATED_<id> flags are still set so we
//     have a record that the migration step ran and so callers downstream
//     (server-side backfills, future migrations) can detect first-touch.

export const STORAGE_PREFIX = 'K_';
export const BROWSER_MIGRATED_FLAG = 'BROWSER_MIGRATED';
export const USER_MIGRATED_PREFIX = 'USER_MIGRATED_';

let currentUserId = null;

export function setCurrentUser(userId) {
  currentUserId = userId || null;
}

// Used by tests / callers that need to know who they're acting as.
export function getCurrentUserId() {
  return currentUserId;
}

function fullKey(key) {
  if (!currentUserId) {
    throw new Error(
      `userStorage called for "${key}" before setCurrentUser(userId). ` +
      `Make sure the caller waits until auth has hydrated.`
    );
  }
  return `${STORAGE_PREFIX}${currentUserId}_${key}`;
}

export const userStorage = {
  get(key) {
    return window.localStorage.getItem(fullKey(key));
  },
  set(key, value) {
    window.localStorage.setItem(fullKey(key), value);
  },
  remove(key) {
    window.localStorage.removeItem(fullKey(key));
  },
};

// Bookkeeping only. As of Phase A1.6 this no longer copies legacy un-prefixed
// keys into the user's namespace — new users (including the first user on a
// device with pre-Supabase data) get a truly empty dashboard. The flags are
// still written so future migrations can tell whether a browser/user has been
// touched before.
export function migrateBrowserData(userId) {
  if (!userId) return { skipped: 'no-user' };
  let ls;
  try {
    ls = window.localStorage;
  } catch {
    return { skipped: 'no-localStorage' };
  }
  ls.setItem(BROWSER_MIGRATED_FLAG, 'true');
  ls.setItem(`${USER_MIGRATED_PREFIX}${userId}`, 'true');
  return { migrated: 0 };
}
